/**
 * studioPRO Enterprise - Router Konfiguration
 *
 * Diese Datei konfiguriert das Vue Router System für die gesamte Anwendung.
 * Sie definiert alle Routen, Navigation Guards und Router-Optionen.
 *
 * Die Routen sind nach Domänen organisiert:
 * - Auth: Authentifizierung und Login
 * - Master Data: Stammdaten (Schüler, Lehrer, Klassen, etc.)
 * - Schedule: Stundenplan und Kalender
 * - Finance: Finanzmodul (Beiträge, Buchungen, etc.)
 * - Attendance: Anwesenheitsverwaltung
 * - Reports: Berichte und Auswertungen
 * - Settings: Systemeinstellungen
 * - Stundenkonto: Lehrer-Stundenkonto-Modul
 *
 * @version 1.0.0
 * @author MiniMax Agent
 */

import { createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth.store'

/**
 * Import aller Views für die Router-Konfiguration
 * Diese werden lazy-loaded für bessere Performance
 */
const LoginView = () => import('@/modules/auth/views/LoginView.vue')
const ForgotPasswordView = () => import('@/modules/auth/views/ForgotPasswordView.vue')
const ResetPasswordView = () => import('@/modules/auth/views/ResetPasswordView.vue')
const PublicAvailabilitySurveyView = () => import('@/views/PublicAvailabilitySurveyView.vue')
const PublicTeacherOnboardingView = () => import('@/views/PublicTeacherOnboardingView.vue')
const PublicStudentOnboardingView = () => import('@/views/PublicStudentOnboardingView.vue')
const AvailabilitySurveyView = () => import('@/modules/availability-survey/views/AvailabilitySurveyView.vue')
const DashboardView = () => import('@/views/DashboardView.vue')
const AdminDashboardView = () => import('@/modules/dashboard/views/AdminDashboardView.vue')
const TeacherDashboardView = () => import('@/modules/dashboard/views/TeacherDashboardView.vue')
const PracticeView = () => import('@/modules/practice/views/PracticeView.vue')
const OfflinePracticeView = () => import('@/modules/practice/views/OfflinePracticeView.vue')
const AvailabilityView = () => import('@/modules/scheduling/views/AvailabilityView.vue')
const MyAvailabilityView = () => import('@/modules/scheduling/views/MyAvailabilityView.vue')
const TeacherCalendarView = () => import('@/modules/scheduling/views/CalendarView.vue')
const AdminLessonSlotsView = () => import('@/modules/scheduling/views/AdminLessonSlotsView.vue')
const AdminCalendarView = () => import('@/modules/scheduling/views/AdminCalendarView.vue')
const AdminAvailabilityOverviewView = () => import('@/modules/scheduling/views/AdminAvailabilityOverviewView.vue')
const CancelledLessonsView = () => import('@/modules/scheduling/views/CancelledLessonsView.vue')
const CoursesView = () => import('@/modules/courses/views/CoursesView.vue')
const CourseDetailView = () => import('@/modules/courses/views/CourseDetailView.vue')
const StudentDashboardView = () => import('@/modules/dashboard/views/StudentDashboardView.vue')
const GuardianDashboardView = () => import('@/modules/dashboard/views/GuardianDashboardView.vue')
const PersonListView = () => import('@/modules/master-data/views/PersonListView.vue')
const StudentsView = () => import('@/modules/master-data/views/StudentsView.vue')
const StudentDetailView = () => import('@/modules/master-data/views/StudentDetailView.vue')
const TeachersView = () => import('@/modules/master-data/views/TeachersView.vue')
const SubjectsView = () => import('@/modules/master-data/views/SubjectsView.vue')
const RoomsView = () => import('@/modules/master-data/views/RoomsView.vue')
const MessagesView = () => import('@/modules/messages/views/MessagesView.vue')
const SettingsView = () => import('@/modules/settings/views/SettingsView.vue')
const NotFoundView = () => import('@/core/views/NotFoundView.vue')
const FerienkalenderView = () => import('@/modules/ferienkalender/views/FerienkalenderView.vue')
const ProbestundenView = () => import('@/modules/probestunden/views/ProbestundenView.vue')
const WiedervorlagenView = () => import('@/modules/wiedervorlagen/views/WiedervorlagenView.vue')
const ScheduleDraftsView = () => import('@/modules/schedule-drafts/views/ScheduleDraftsView.vue')
const ContractsView = () => import('@/modules/contracts/views/ContractsView.vue')
const CardsView = () => import('@/modules/cards/views/CardsView.vue')
const InvoicesView = () => import('@/modules/billing/views/InvoicesView.vue')
const TeacherPayView = () => import('@/modules/teacher-pay/views/TeacherPayView.vue')

/**
 * Alle Routen der Anwendung
 * Definiert als Array von RouteRecordRaw-Objekten
 */
const routes: RouteRecordRaw[] = [
  /**
   * Authentifizierungs-Routen
   * Diese Routes sind öffentlich zugänglich
   */
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: {
      title: 'Anmelden',
      requiresAuth: false,
      layout: 'auth'
    }
  },
  {
    // „Passwort vergessen“, siehe backend/src/utils/password-reset.ts
    path: '/passwort-vergessen',
    name: 'ForgotPassword',
    component: ForgotPasswordView,
    meta: {
      title: 'Passwort vergessen',
      requiresAuth: false,
      layout: 'auth'
    }
  },
  {
    path: '/passwort-zuruecksetzen',
    name: 'ResetPassword',
    component: ResetPasswordView,
    meta: {
      title: 'Neues Passwort',
      requiresAuth: false,
      layout: 'auth'
    }
  },
  {
    path: '/dozent-aufnahme/:token',
    name: 'PublicTeacherOnboarding',
    component: PublicTeacherOnboardingView,
    meta: {
      title: 'Aufnahme als Dozent:in',
      requiresAuth: false,
      layout: 'auth'
    }
  },
  {
    path: '/anmeldung/:token',
    name: 'PublicStudentOnboarding',
    component: PublicStudentOnboardingView,
    meta: {
      title: 'Anmeldung zum Unterricht',
      requiresAuth: false,
      layout: 'auth'
    }
  },
  {
    path: '/verfuegbarkeit/:token',
    name: 'PublicAvailabilitySurvey',
    component: PublicAvailabilitySurveyView,
    meta: {
      title: 'Verfügbarkeit eintragen',
      requiresAuth: false,
      layout: 'auth'
    }
  },

  /**
   * Rollen-Dashboards (interner Bereich)
   * Je ein eigenes Dashboard für Admin/Dozent/Schüler - siehe
   * backend/src/routes/dashboard.ts für die zugehörigen Endpunkte.
   */
  {
    path: '/dashboard/admin',
    name: 'AdminDashboard',
    component: AdminDashboardView,
    meta: { title: 'Admin-Dashboard', requiresAuth: true, requiresRole: 'admin', layout: 'app' }
  },
  {
    path: '/dashboard/teacher',
    name: 'TeacherDashboard',
    component: TeacherDashboardView,
    meta: { title: 'Dozenten-Dashboard', requiresAuth: true, requiresRole: 'teacher', layout: 'app' }
  },
  {
    path: '/dashboard/student',
    name: 'StudentDashboard',
    component: StudentDashboardView,
    meta: { title: 'Schüler-Dashboard', requiresAuth: true, requiresRole: 'student', layout: 'app' }
  },
  {
    path: '/dashboard/guardian',
    name: 'GuardianDashboard',
    component: GuardianDashboardView,
    meta: { title: 'Familienbereich', requiresAuth: true, requiresRole: 'guardian', layout: 'app' }
  },
  {
    path: '/availability',
    name: 'Availability',
    component: AvailabilityView,
    meta: { title: 'Verfügbarkeiten', requiresAuth: true, requiresRole: 'teacher', layout: 'app' }
  },
  {
    path: '/my-availability',
    name: 'MyAvailability',
    component: MyAvailabilityView,
    meta: { title: 'Meine Verfügbarkeit', requiresAuth: true, requiresRole: ['student', 'guardian'], layout: 'app' }
  },
  {
    path: '/my-calendar',
    name: 'TeacherCalendar',
    component: TeacherCalendarView,
    meta: { title: 'Mein Kalender', requiresAuth: true, requiresRole: 'teacher', layout: 'app' }
  },
  {
    path: '/courses',
    name: 'Courses',
    component: CoursesView,
    meta: { title: 'Kurse', requiresAuth: true, requiresRole: 'admin', layout: 'app' }
  },
  {
    path: '/courses/:id',
    name: 'CourseDetail',
    component: CourseDetailView,
    meta: { title: 'Kurs', requiresAuth: true, requiresRole: 'admin', layout: 'app' }
  },
  {
    path: '/practice',
    name: 'Practice',
    component: PracticeView,
    meta: { title: 'Übungsmaterial', requiresAuth: true, requiresRole: ['teacher', 'admin'], layout: 'app' }
  },
  {
    // Übungsbereich, der auch ohne Server läuft (gespeicherte Dateien, Metronom, Selbstaufnahme)
    path: '/practice-offline',
    name: 'PracticeOffline',
    component: OfflinePracticeView,
    meta: { title: 'Üben (offline)', requiresAuth: true, requiresRole: ['student', 'guardian', 'teacher', 'admin'], layout: 'app', standalone: true, offlineOk: true }
  },
  {
    path: '/my-courses',
    name: 'TeacherCourses',
    component: CoursesView,
    meta: { title: 'Meine Kurse', requiresAuth: true, requiresRole: 'teacher', layout: 'app' }
  },
  {
    path: '/my-courses/:id',
    name: 'TeacherCourseDetail',
    component: CourseDetailView,
    meta: { title: 'Kurs', requiresAuth: true, requiresRole: 'teacher', layout: 'app' }
  },
  {
    path: '/lesson-slots',
    name: 'AdminLessonSlots',
    component: AdminLessonSlotsView,
    meta: { title: 'Unterrichtstermine', requiresAuth: true, requiresRole: 'admin', layout: 'app' }
  },
  {
    path: '/admin-calendar',
    name: 'AdminCalendar',
    component: AdminCalendarView,
    meta: { title: 'Admin-Kalender', requiresAuth: true, requiresRole: 'admin', layout: 'app' }
  },
  {
    path: '/teacher-schedules',
    name: 'AdminAvailabilityOverview',
    component: AdminAvailabilityOverviewView,
    meta: { title: 'Verfügbarkeiten & Abwesenheiten', requiresAuth: true, requiresRole: 'admin', layout: 'app' }
  },
  {
    path: '/cancelled-lessons',
    name: 'CancelledLessons',
    component: CancelledLessonsView,
    meta: { title: 'Ausgefallene Stunden', requiresAuth: true, requiresRole: 'admin', layout: 'app' }
  },

  /**
   * Haupt-Routen der Anwendung
   * Alle diese Routen erfordern eine Authentifizierung
   */
  {
    path: '/',
    name: 'Dashboard',
    component: DashboardView,
    meta: {
      title: 'Dashboard',
      requiresAuth: true,
      // Zeigt seit 11.09. echte Kennzahlen über admin-only Endpunkte
      // (siehe backend dashboard.ts /admin) - ohne diese Einschränkung
      // würden Dozenten/Schüler, die die URL direkt aufrufen, nur
      // fehlschlagende Requests sehen (kein eigenes Dashboard-Layout dafür).
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['dashboard.view']
    }
  },

  /**
   * Master Data Module - Personenverwaltung
   * Zentrale Verwaltung aller Personenstammdaten
   */
  {
    path: '/persons',
    name: 'Persons',
    component: PersonListView,
    meta: {
      title: 'Personen',
      requiresAuth: true,
      layout: 'app',
      permissions: ['persons.view']
    }
  },

  /**
   * Master Data Module
   * Verwaltung von Schülern, Lehrern, Klassen, Fächern und Räumen
   */
  {
    path: '/students',
    name: 'Students',
    component: StudentsView,
    meta: {
      title: 'Schüler',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['students.view']
    }
  },
  {
    path: '/students/:id',
    name: 'StudentDetail',
    component: StudentDetailView,
    meta: {
      title: 'Schülerdetails',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['students.view']
    }
  },
  {
    path: '/teachers',
    name: 'Teachers',
    component: TeachersView,
    meta: {
      title: 'Lehrer',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['teachers.view']
    }
  },
  {
    // "Klassen" und "Fächer" waren zwei fast identische, rein hartcodierte
    // Attrappen-Seiten (dieselbe Instrumentenliste) - jetzt zu einer
    // einzigen echten Seite zusammengelegt. /classes bleibt als Weiterleitung
    // bestehen, damit alte Links/Lesezeichen nicht ins Leere laufen.
    path: '/classes',
    redirect: '/subjects'
  },
  {
    path: '/subjects',
    name: 'Subjects',
    component: SubjectsView,
    meta: {
      title: 'Fächer',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['subjects.view']
    }
  },
  {
    path: '/rooms',
    name: 'Rooms',
    component: RoomsView,
    meta: {
      title: 'Räume',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['rooms.view']
    }
  },


  {
    path: '/messages',
    name: 'Messages',
    component: MessagesView,
    meta: {
      title: 'Nachrichten',
      requiresAuth: true,
      layout: 'app'
    }
  },


  /**
   * Settings Module
   * Systemeinstellungen und Benutzerprofil
   */
  {
    path: '/settings',
    name: 'Settings',
    component: SettingsView,
    meta: {
      title: 'Einstellungen',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['settings.view']
    }
  },


  /**
   * Ferienkalender Module
   * Verwaltung von Ferien und Feiertagen
   */
  {
    path: '/ferienkalender',
    name: 'Ferienkalender',
    component: FerienkalenderView,
    meta: {
      title: 'Ferienkalender',
      requiresAuth: true,
      layout: 'app',
      permissions: ['ferienkalender.view']
    }
  },

  // Hinweis: /room (Duplikat von /rooms) wurde entfernt - siehe /rooms für die
  // konsolidierte Raumverwaltung.

  /**
   * Probestunden Module
   * Verwaltung von Probestunden
   */
  {
    path: '/probestunden',
    name: 'Probestunden',
    component: ProbestundenView,
    meta: {
      title: 'Probestunden',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['probestunden.view']
    }
  },
  {
    path: '/wiedervorlagen',
    name: 'Wiedervorlagen',
    component: WiedervorlagenView,
    meta: {
      title: 'Wiedervorlagen',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app'
    }
  },
  {
    path: '/schedule-drafts',
    name: 'ScheduleDrafts',
    component: ScheduleDraftsView,
    meta: {
      title: 'Automatischer Stundenplan',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app'
    }
  },
  {
    path: '/availability-survey',
    name: 'AvailabilitySurvey',
    component: AvailabilitySurveyView,
    meta: {
      title: 'Verfügbarkeits-Abfrage',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app'
    }
  },

  /**
   * Verträge
   * Laufende Unterrichtsverträge (monatlich/quartalsweise/jährlich)
   */
  {
    path: '/contracts',
    name: 'Contracts',
    component: ContractsView,
    meta: {
      title: 'Verträge',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['contracts.view']
    }
  },

  /**
   * Karten
   * 5er-/10er-Unterrichtskarten
   */
  {
    path: '/cards',
    name: 'Cards',
    component: CardsView,
    meta: {
      title: 'Karten',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['cards.view']
    }
  },

  /**
   * Abrechnung
   * Rechnungslauf aus Vertragspauschalen und Kartennutzung
   */
  {
    path: '/billing',
    name: 'Billing',
    component: InvoicesView,
    meta: {
      title: 'Abrechnung',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app',
      permissions: ['billing.view']
    }
  },

  /**
   * Honorarabrechnung
   * Monatslauf je Honorarkraft: Entwurf prüfen, festschreiben, als bezahlt vermerken, PDF
   */
  {
    path: '/teacher-pay',
    name: 'TeacherPay',
    component: TeacherPayView,
    meta: {
      title: 'Honorarabrechnung',
      requiresAuth: true,
      requiresRole: 'admin',
      layout: 'app'
    }
  },

  /**
   * 404 - Not Found Route
   * Fängt alle nicht definierten Routen ab
   */
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundView,
    meta: {
      title: 'Seite nicht gefunden',
      requiresAuth: false,
      layout: 'auth'
    }
  }
]

/**
 * Router-Instanz erstellen
 * Verwendung von createWebHistory für HTML5 History Mode
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    // Zum Seitenanfang scrollen bei Navigation
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

/**
 * Navigation Guard - Authentifizierung prüfen
 * Vor jeder Navigation wird geprüft, ob der Benutzer authentifiziert ist
 */
router.beforeEach(async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const authStore = useAuthStore()

  // Nach einem Seiten-Reload ist das Token aus localStorage schon da, aber
  // der Nutzer (noch) nicht geladen - einmalig gegen das Backend prüfen, ob
  // es noch gültig ist, statt es blind als aktiv anzunehmen.
  if (authStore.token && !authStore.user) {
    await authStore.restoreSession()
  }

  // Server nicht erreichbar (Sitzung aus dem Zwischenspeicher): nur der Offline-Bereich ist nutzbar - alles andere
  // würde nur Fehlermeldungen zeigen
  if (authStore.offlineSession && authStore.isAuthenticated && !to.meta.offlineOk && to.meta.requiresAuth) {
    next({ name: 'PracticeOffline' })
    return
  }

  // Dokumententitel setzen
  document.title = `${to.meta.title || 'studioPRO'} | studioPRO Enterprise`

  // Prüfen ob Route Authentifizierung erfordert
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Benutzer nicht authentifiziert -> Loginseite
    next({ name: 'Login', query: { redirect: to.fullPath } })
  } else if ((to.name === 'Login' || to.name === 'ForgotPassword') && authStore.isAuthenticated) {
    // Bereits authentifiziert und versucht Login-Seite aufzurufen -> eigenes Dashboard
    // (Passwort ändern geht dort über das Konto-Menü)
    next(authStore.getRedirectPath())
  } else if (to.name === 'ResetPassword' && authStore.isAuthenticated) {
    // Link aus der Mail in einem Browser, in dem noch jemand angemeldet ist:
    // vorher abmelden, damit die Seite ohne Verwaltungsrahmen erscheint (das
    // neue Passwort beendet ohnehin alle Sitzungen)
    await authStore.logout()
    next()
  } else if (
    to.meta.requiresRole &&
    authStore.isAuthenticated &&
    ![to.meta.requiresRole].flat().includes(authStore.user?.role as string)
  ) {
    // Falsche Rolle für dieses Dashboard -> zum eigenen Dashboard
    next(authStore.getRedirectPath())
  } else {
    next()
  }
})

/**
 * Navigation Guard - After Hook
 * Für Analytics, Logging, etc.
 */
router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized): void => {
  // Hier können Tracking-Informationen gespeichert werden
  console.log(`Navigation: ${from.path} -> ${to.path}`)
})

export default router

/**
 * Export der Router-Instanz für externe Nutzung
 */
export { router }
